import React, { useRef, useCallback } from "react";
import { View, StyleSheet, ScrollView, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import type { NavigationItem } from "@/types/contexts/navigationHistory";
import { MaterialIcons } from "@expo/vector-icons";
import { useSegments } from "expo-router";
import { PreviewIcon } from "./PreviewIcon";
import { useToolTip } from "@/hooks/useToolTip";
import useAuth from "@/hooks/useAuth";
import useNavigationGuard from "@/hooks/useNavigationGuard";

export interface BreadcrumbNavigationProps {
  currentTitle: string;
  currentPanel?: string;
  currentId?: string;
}

interface BreadcrumbItemProps {
  item: NavigationItem;
  onPress?: () => void;
  isLast: boolean;
  entityDisplayName?: string;
}

function BreadcrumbItem({ item, onPress, isLast, entityDisplayName }: BreadcrumbItemProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const tooltipText = entityDisplayName || item.panel;

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>{tooltipText}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + width / 2 - 30, y: y - 24 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltipText, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <View style={styles.breadcrumbWrapper}>
      <Pressable
        ref={ref}
        onPress={() => {
          toolTip.setVisible(false);
          onPress?.();
        }}
        disabled={!onPress || isLast}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        style={({ pressed }) => [
          styles.breadcrumbItem,
          { backgroundColor: pressed && !isLast ? colors.backgroundSecondary : "transparent" },
          isLast && styles.currentItem,
        ]}
      >
        {({ pressed }) => (
          <View style={styles.breadcrumbContent}>
            <PreviewIcon entityType={item.panel} size={16} iconColor={isLast ? colors.text : colors.primary} />
            <ThemedText
              type={isLast ? "defaultSemiBold" : "link"}
              style={[
                styles.breadcrumbText,
                isLast && { opacity: 1, color: colors.text },
                pressed && !isLast && { opacity: 0.6 },
              ]}
            >
              {item.title || item.panel}
            </ThemedText>
          </View>
        )}
      </Pressable>
      {!isLast && <MaterialIcons color={colors.text} size={20} name="chevron-right" style={styles.chevron} />}
    </View>
  );
}

// Map panel names to displayNames keys
const panelToDisplayNameKey: Record<string, string> = {
  project: "projects",
  shift: "shifts",
  user: "users",
  client: "clients",
  contact: "contacts",
  time_card: "time_cards",
  course: "courses",
  incident: "incidents",
  investigation: "investigations",
  invoice: "invoices",
  estimate: "estimates",
  application: "applications",
};

export function BreadcrumbNavigation({ currentTitle, currentPanel, currentId }: BreadcrumbNavigationProps) {
  const { navHistory, popNavigation } = useNavigationHistory();
  const { displayNames } = useAuth();
  const { navigateToEntity } = useNavigationGuard();
  const segs = useSegments();

  // Don't render breadcrumbs in full-screen mode - native back navigation works there
  const isFullScreen = (segs as string[]).includes("[id]");
  if (isFullScreen) return null;

  const getEntityDisplayName = (panel: string): string => {
    const key = panelToDisplayNameKey[panel];
    if (key && displayNames?.[key as keyof typeof displayNames]) {
      return displayNames[key as keyof typeof displayNames].name;
    }
    // Capitalize first letter as fallback
    return panel.charAt(0).toUpperCase() + panel.slice(1).replace(/_/g, " ");
  };

  const handleBreadcrumbClick = (index: number) => {
    // Pop history items after the clicked one
    const clickedItem = navHistory[index];
    if (!clickedItem) return;

    // Validate item before navigating - reject undefined/invalid values
    if (!clickedItem.panel || clickedItem.panel === "undefined" || !clickedItem.id || clickedItem.id === "undefined") {
      return;
    }

    // Remove all items after this index
    for (let i = navHistory.length - 1; i > index; i--) {
      popNavigation();
    }

    // Navigate to the clicked item
    navigateToEntity(clickedItem.panel, clickedItem.id);

    // Pop the clicked item itself
    popNavigation();
  };

  // Don't show breadcrumbs if there's no history
  if (navHistory.length === 0) return null;

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {navHistory.map((item, index) => (
          <BreadcrumbItem
            key={`${item.panel}-${item.id}-${index}`}
            item={item}
            onPress={() => handleBreadcrumbClick(index)}
            isLast={false}
            entityDisplayName={getEntityDisplayName(item.panel)}
          />
        ))}
        {/* Current screen - final item, not clickable */}
        {/* <BreadcrumbItem */}
        {/*   item={{ */}
        {/*     panel: currentPanel || "", */}
        {/*     id: currentId || "", */}
        {/*     title: currentTitle, */}
        {/*   }} */}
        {/*   isLast={true} */}
        {/* /> */}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginLeft: 16,
    marginBottom: 8,
    paddingRight: 16,
    flexDirection: "row",
    alignItems: "center",
    marginTop: -16,
  },
  scrollContent: {
    alignItems: "center",
    gap: 0,
  },
  breadcrumbWrapper: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbItem: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
  },
  breadcrumbText: {
    fontSize: 14,
  },
  breadcrumbContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  currentItem: {
    // cursor: "default",
  },
  chevron: {
    opacity: 0.5,
    marginHorizontal: 2,
  },
});
